import { useEffect, useRef, useState } from 'react';
import AgencyIcon from './AgencyIcon';

export function AgencyImage({ src, alt, className = '', loading }) {
    const [failedSrc, setFailedSrc] = useState(null);
    if (!src || failedSrc === src) return <div className={`ef-agency-image-fallback ${className}`} role={alt ? 'img' : undefined} aria-label={alt || undefined} aria-hidden={!alt || undefined}><AgencyIcon name="building" /><span>Immagine non disponibile</span></div>;
    return <img src={src} alt={alt} className={className} loading={loading} onError={() => setFailedSrc(src)} />;
}

export default function AgencyGallery({ photos = [], title }) {
    const [index, setIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const dialogRef = useRef(null);
    const openButtonRef = useRef(null);
    const touchRef = useRef(null);
    const activeIndex = Math.min(index, Math.max(0, photos.length - 1));
    const move = delta => {
        if (photos.length < 2) return;
        setIndex(current => (Math.min(current, photos.length - 1) + delta + photos.length) % photos.length);
    };

    useEffect(() => {
        if (!expanded || !photos.length) return undefined;
        const dialog = dialogRef.current;
        const openButton = openButtonRef.current;
        dialog.showModal();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            dialog.close();
            document.body.style.overflow = previousOverflow;
            openButton?.focus();
        };
    }, [expanded, photos.length]);

    const handleTouchStart = event => {
        touchRef.current = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    };
    const handleTouchEnd = event => {
        const start = touchRef.current;
        touchRef.current = null;
        if (!start || !event.changedTouches.length || photos.length < 2) return;
        const dx = event.changedTouches[0].clientX - start.x;
        const dy = event.changedTouches[0].clientY - start.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
    };
    const handleKeys = event => {
        if (photos.length < 2 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
    };
    const controls = photos.length > 1 && <>
        <button type="button" className="ef-agency-gallery__prev" aria-label="Foto precedente" onClick={() => move(-1)}><AgencyIcon name="left" /></button>
        <button type="button" className="ef-agency-gallery__next" aria-label="Foto successiva" onClick={() => move(1)}><AgencyIcon name="right" /></button>
    </>;

    if (!photos.length) return null;

    return <section className="ef-agency-gallery" aria-label={`Foto di ${title}`} onKeyDown={handleKeys}>
        <div className="ef-agency-gallery__stage" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchCancel={() => { touchRef.current = null; }}>
            <AgencyImage key={photos[activeIndex]} src={photos[activeIndex]} alt={`${title}, foto ${activeIndex + 1}`} />
            {controls}
            <button ref={openButtonRef} type="button" className="ef-agency-gallery__expand" onClick={() => setExpanded(true)}><AgencyIcon name="expand" /> Ingrandisci</button>
            <span className="ef-agency-gallery__count" aria-live="polite" aria-atomic="true">{activeIndex + 1} / {photos.length}</span>
        </div>
        {photos.length > 1 && <div className="ef-agency-gallery__thumbnails" aria-label="Scegli una foto">
            {photos.map((src, photoIndex) => <button type="button" key={`${src}-${photoIndex}`} aria-label={`Mostra foto ${photoIndex + 1}`} aria-pressed={photoIndex === activeIndex} onClick={() => setIndex(photoIndex)}>
                <AgencyImage src={src} alt="" loading="lazy" />
            </button>)}
        </div>}
        <dialog ref={dialogRef} className="ef-agency-lightbox" aria-label={`Galleria di ${title}`} onCancel={() => setExpanded(false)} onClick={event => { if (event.target === event.currentTarget) setExpanded(false); }}>
            <div className="ef-agency-lightbox__inner" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchCancel={() => { touchRef.current = null; }}>
                <button type="button" className="ef-agency-lightbox__close" aria-label="Chiudi la galleria" onClick={() => setExpanded(false)}><AgencyIcon name="close" /></button>
                {expanded && <AgencyImage key={photos[activeIndex]} src={photos[activeIndex]} alt={`${title}, foto ${activeIndex + 1}`} />}
                {controls}
                <p aria-live="polite" aria-atomic="true">{title} · {activeIndex + 1} / {photos.length}</p>
            </div>
        </dialog>
    </section>;
}
